export interface SearchableFoodResult {
  readonly name: string;
}

/**
 * Orders matches by how closely the food name resembles the entered query.
 * The provider's original order is retained whenever two results have the
 * same relevance rank.
 */
export function rankFoodSearchResults<T extends SearchableFoodResult>(
  query: string,
  results: readonly T[],
): T[] {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [...results];
  const queryTokens = normalizedQuery.split(" ");

  return results
    .map((result, index) => ({
      index,
      result,
      rank: relevanceRank(normalizedQuery, queryTokens, result.name),
    }))
    .sort((left, right) => compareRanks(left.rank, right.rank) || left.index - right.index)
    .map(({ result }) => result);
}

function relevanceRank(normalizedQuery: string, queryTokens: string[], name: string): number[] {
  const normalizedName = normalizeSearchText(name);
  if (!normalizedName) return [queryTokens.length, Number.MAX_SAFE_INTEGER];

  const nameTokens = normalizedName.split(" ");
  const extraTokens = Math.max(0, nameTokens.length - queryTokens.length);
  const editRatio = normalizedEditDistance(normalizedQuery, normalizedName);
  const exactPositions = tokenPositions(queryTokens, nameTokens, (queryToken, nameToken) => queryToken === nameToken);
  const relatedPositions = relatedTokenPositions(queryTokens, nameTokens);
  const matchedPositions = relatedPositions.filter((position) => position >= 0);
  const unmatchedTokens = queryTokens.length - matchedPositions.length;
  const nearMatches = relatedPositions.reduce(
    (count, position, index) => count + (
      position >= 0 && nameTokens[position] !== queryTokens[index] ? 1 : 0
    ),
    0,
  );
  const exactSequenceIndex = contiguousPhraseIndex(nameTokens, queryTokens, (queryToken, nameToken) => queryToken === nameToken);
  const relatedSequenceIndex = contiguousPhraseIndex(nameTokens, queryTokens, relatedToken);
  const exactAll = exactPositions.every((position) => position >= 0);
  const relatedAll = relatedPositions.every((position) => position >= 0);

  let phraseClass: number;
  if (normalizedName === normalizedQuery) phraseClass = 0;
  else if (exactSequenceIndex === 0) phraseClass = 1;
  else if (relatedSequenceIndex === 0) phraseClass = 2;
  else if (exactSequenceIndex > 0) phraseClass = 3;
  else if (relatedSequenceIndex > 0) phraseClass = 4;
  else if (exactAll && isAscending(exactPositions)) phraseClass = 5;
  else if (relatedAll && isAscending(relatedPositions)) phraseClass = 6;
  else if (exactAll) phraseClass = 7;
  else if (relatedAll) phraseClass = 8;
  else phraseClass = 9;

  const orderPenalty = matchedPositions.reduce(
    (penalty, position, index) => penalty + (index > 0 && position < matchedPositions[index - 1]! ? 1 : 0),
    0,
  );
  const firstPosition = matchedPositions.length > 0 ? Math.min(...matchedPositions) : Number.MAX_SAFE_INTEGER;
  const span = matchedPositions.length > 0 ? Math.max(...matchedPositions) - firstPosition + 1 : 0;
  const spanGap = Math.max(0, span - matchedPositions.length);
  return [
    unmatchedTokens,
    extraTokens,
    phraseClass,
    nearMatches,
    orderPenalty,
    spanGap,
    firstPosition,
    editRatio,
    normalizedName.length,
  ];
}

function contiguousPhraseIndex(
  nameTokens: string[],
  queryTokens: string[],
  matches: (queryToken: string, nameToken: string) => boolean,
): number {
  const lastStart = nameTokens.length - queryTokens.length;
  for (let start = 0; start <= lastStart; start += 1) {
    if (queryTokens.every((token, offset) => matches(token, nameTokens[start + offset]!))) return start;
  }
  return -1;
}

function tokenPositions(
  queryTokens: string[],
  nameTokens: string[],
  matches: (queryToken: string, nameToken: string) => boolean,
): number[] {
  const used = new Set<number>();
  return queryTokens.map((queryToken) => {
    const position = nameTokens.findIndex((nameToken, index) => !used.has(index) && matches(queryToken, nameToken));
    if (position >= 0) used.add(position);
    return position;
  });
}

function relatedTokenPositions(queryTokens: string[], nameTokens: string[]): number[] {
  const used = new Set<number>();
  return queryTokens.map((queryToken) => {
    let position = nameTokens.findIndex((nameToken, index) => !used.has(index) && queryToken === nameToken);
    if (position < 0) {
      position = nameTokens.findIndex((nameToken, index) => !used.has(index) && relatedToken(queryToken, nameToken));
    }
    if (position >= 0) used.add(position);
    return position;
  });
}

function relatedToken(queryToken: string, nameToken: string): boolean {
  if (queryToken === nameToken) return true;
  const longest = Math.max(queryToken.length, nameToken.length);
  if (Math.min(queryToken.length, nameToken.length) < 3) return false;
  return levenshteinDistance(queryToken, nameToken) <= Math.max(1, Math.floor(longest * 0.2));
}

function isAscending(positions: number[]): boolean {
  return positions.every((position, index) => index === 0 || position > positions[index - 1]!);
}

function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function normalizedEditDistance(left: string, right: string): number {
  const longest = Math.max(left.length, right.length);
  return longest === 0 ? 0 : Math.round((levenshteinDistance(left, right) / longest) * 1_000);
}

function levenshteinDistance(left: string, right: string): number {
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const substitution = previous[rightIndex - 1]! + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1);
      current[rightIndex] = Math.min(
        previous[rightIndex]! + 1,
        current[rightIndex - 1]! + 1,
        substitution,
      );
    }
    previous = current;
  }
  return previous[right.length]!;
}

function compareRanks(left: number[], right: number[]): number {
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (left[index] ?? 0) - (right[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}
